import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  Avatar,
  Skeleton,
} from "@mui/material";
import sh from "bundles/common/utils/sh";
import NotesPanel from "bundles/Internal/components/common/NotesPanel";
import { ArrowLeft } from "lucide-react";
import {
  RESOURCE_TYPE_COLOR,
  LOCALE_COLOR,
  CATEGORY_COLOR,
} from "bundles/Internal/components/blog_post/BlogPostTable";

const DATE_KEYS = new Set(["created_at", "updated_at"]);

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("fr-BE", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
};

const pill = (label, c) => (
  <Chip
    label={String(label).replaceAll("_", " ")}
    size="small"
    sx={{
      backgroundColor: c.bg,
      color: c.text,
      fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
      fontSize: 11,
      fontWeight: 600,
      borderRadius: "8px",
      height: 24,
    }}
  />
);

const formatBlogPostCell = (blogPost, key) => {
  const value = blogPost?.[key];
  if (value === null || value === undefined || value === "") return "—";

  if (DATE_KEYS.has(key)) return formatDateTime(value);

  if (key === "online") {
    return value ? "Online" : "Offline";
  }

  if (key === "resource_type" && value) {
    return pill(value, RESOURCE_TYPE_COLOR[value] || RESOURCE_TYPE_COLOR.hybrid);
  }

  if (key === "preferred_locale" && value) {
    return pill(value, LOCALE_COLOR[value] || LOCALE_COLOR.en);
  }

  if (key === "category" && value) {
    return pill(value, CATEGORY_COLOR[value] || CATEGORY_COLOR.career_advise);
  }

  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const sectionPaperSx = {
  borderRadius: "0.75rem",
  overflow: "hidden",
  border: "1px solid hsl(140 8% 88%)",
  p: 4,
};

const sectionTitleSx = {
  color: "hsl(150 30% 12%)",
  fontWeight: 600,
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  mb: 3,
  display: "flex",
  alignItems: "center",
  gap: 1,
};

const KeyValueTile = ({ label, value }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: "calc(0.75rem - 2px)",
      backgroundColor: "hsl(140 10% 95%)",
      height: "100%",
    }}
  >
    <Typography
      sx={{
        color: "hsl(150 10% 46%)",
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        fontWeight: 600,
        fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
      }}
    >
      {label}
    </Typography>
    <Box
      sx={{
        color: "hsl(150 30% 12%)",
        mt: 0.5,
        fontSize: 14,
        fontFamily: "Segoe UI Regular, sans-serif, system-ui",
      }}
    >
      {value}
    </Box>
  </Box>
);

const DETAILS_FIELDS = [
  { key: "id", label: "ID" },
  { key: "online", label: "Online" },
  { key: "title", label: "Title" },
  { key: "resource_type", label: "Resource Type" },
  { key: "preferred_locale", label: "Preferred Locale" },
  { key: "category", label: "Category" },
  { key: "view_count", label: "View Count" },
  { key: "created_at", label: "Created At" },
  { key: "updated_at", label: "Updated At" },
];

function DetailsTab({ blogPost, loading }) {
  if (loading) {
    return (
      <Grid container spacing={3}>
        {[...Array(9)].map((_, idx) => (
          <Grid item xs={12} sm={6} key={idx}>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="text" width="60%" />
          </Grid>
        ))}
      </Grid>
    );
  }
  if (!blogPost) return null;

  return (
    <Stack spacing={3}>
      {/* Basic Info */}
      <Grid container spacing={3} alignItems="stretch">
        {DETAILS_FIELDS.map((field) => {
          const value = formatBlogPostCell(blogPost, field.key);
          if (value === "—" || value === "" || value === null || value === undefined) return null;
          return (
            <Grid item xs={12} sm={6} key={field.key}>
              <KeyValueTile label={field.label} value={value} />
            </Grid>
          );
        })}
      </Grid>

      {/* Cover Picture */}
      {blogPost.cover_picture_url && (
        <Box>
          <Typography sx={sectionTitleSx}>Cover Picture</Typography>
          <Box
            component="img"
            src={blogPost.cover_picture_url}
            alt={blogPost.title}
            sx={{
              width: "100%",
              maxWidth: 600,
              height: "auto",
              borderRadius: "8px",
              objectFit: "cover",
            }}
          />
        </Box>
      )}

      {/* Content */}
      {blogPost.content && (
        <Box>
          <Typography sx={sectionTitleSx}>Content</Typography>
          <Box
            sx={{
              p: 3,
              borderRadius: "8px",
              backgroundColor: "#fff",
              border: "1px solid hsl(140 8% 88%)",
              "& h1, & h2, & h3, & h4, & h5, & h6": {
                fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                color: "hsl(150 30% 12%)",
                mt: 2,
                mb: 1,
              },
              "& p": {
                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                color: "hsl(150 30% 12%)",
                lineHeight: 1.6,
                mb: 1,
              },
              "& ul, & ol": {
                pl: 3,
                mb: 1,
              },
              "& li": {
                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                color: "hsl(150 30% 12%)",
                lineHeight: 1.6,
              },
            }}
            dangerouslySetInnerHTML={{ __html: blogPost.content }}
          />
        </Box>
      )}
    </Stack>
  );
}

function BlogPostShowPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blogPost, setBlogPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const goBack = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/internal/blog_posts"));

  useEffect(() => {
    if (!id) return;

    setLoading(true);
    setError(null);

    sh.get(`/internal/blog_posts/${id}`)
      .then((res) => {
        setBlogPost(res.data);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        if (err?.response?.status === 401) {
          window.location.href = "/internal/sign_in";
          return;
        }
        setError("Failed to load blog post");
      });
  }, [id]);

  const title = useMemo(() => {
    if (!blogPost) return "";
    return blogPost.title || `Blog Post #${blogPost.id}`;
  }, [blogPost]);

  const getInitials = () => {
    if (!blogPost || !blogPost.title) return "BP";
    const words = blogPost.title.split(" ").filter(Boolean);
    if (words.length === 0) return "BP";
    if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  };

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 72px)",
        backgroundColor: "hsl(140 10% 95%)",
      }}
    >
      {/* Sticky Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 30,
          backgroundColor: "rgba(255,255,255,0.85)",
          backdropFilter: "blur(16px)",
          borderBottom: "1px solid hsl(140 8% 88%)",
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ py: 1.5 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ minWidth: 0 }}
            >
              <Button
                onClick={goBack}
                disableRipple
                sx={{
                  minWidth: 0,
                  p: 0.75,
                  color: "hsl(150 10% 46%)",
                  "&:hover": { color: "hsl(150 30% 12%)", backgroundColor: "transparent" },
                }}
              >
                <ArrowLeft size={20} />
              </Button>

              {loading ? (
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Box>
                    <Skeleton width={140} height={20} />
                    <Skeleton width={200} height={14} />
                  </Box>
                </Stack>
              ) : (
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      fontSize: 15,
                      fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                      fontWeight: 600,
                      backgroundColor: "hsl(140 50% 45%)",
                      color: "#fff",
                    }}
                  >
                    {getInitials()}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: 16,
                        fontWeight: 600,
                        fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                        color: "hsl(150 30% 12%)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "hsl(150 10% 46%)",
                        fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                      }}
                    >
                      Blog Post #{blogPost?.id}
                    </Typography>
                  </Box>
                </Stack>
              )}
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Grid container spacing={3}>
          {/* Left Column - Tabs */}
          <Grid item xs={12} lg={9}>
            <Paper sx={sectionPaperSx}>
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
                sx={{
                  mb: 3,
                  borderBottom: "1px solid hsl(140 8% 88%)",
                  "& .MuiTab-root": {
                    fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                    fontSize: 13,
                    textTransform: "none",
                    color: "hsl(150 10% 46%)",
                    "&.Mui-selected": {
                      color: "hsl(140 50% 45%)",
                    },
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: "hsl(140 50% 45%)",
                  },
                }}
              >
                <Tab label="Details" />
              </Tabs>

              {activeTab === 0 && <DetailsTab blogPost={blogPost} loading={loading} />}
            </Paper>
          </Grid>

          {/* Right Column - Notes */}
          <Grid item xs={12} lg={3}>
            <NotesPanel
              resource="blog_posts"
              recordId={id}
              onNoteAdded={() => {
                if (blogPost) {
                  setBlogPost({
                    ...blogPost,
                    notes_count: (blogPost.notes_count || 0) + 1,
                  });
                }
              }}
              onNoteArchived={() => {
                if (blogPost) {
                  setBlogPost({
                    ...blogPost,
                    notes_count: Math.max((blogPost.notes_count || 0) - 1, 0),
                  });
                }
              }}
            />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default BlogPostShowPage;
